/**
 * Fixed 2026 NFL fantasy calendar (weeks 1–17). Matches backend nfl_season_calendar.py.
 * Week 18 is not used; all leagues finish by week 17.
 */

import config from "../config";
import { parseEasternWallClock } from "./draftTimeUtils";

export const SEASON_YEAR = 2026;
export const WEEK_1_SUNDAY = "2026-09-13";
export const WEEK_1_THURSDAY = "2026-09-10";
/** User-facing: when Week 1 scoring begins (first NFL kickoffs). */
export const WEEK_1_SCORING_START_LABEL = "Thursday, September 10, 2026";
export const WEEK_1_SCORING_START_SHORT = "Thursday, Sept 10, 2026";
export const WEEK_1_SCORING_START_DETAIL =
  "8:15 PM Eastern on Thursday, September 10, 2026 (first NFL kickoffs)";
/** @deprecated Use WEEK_1_SCORING_START_LABEL for user-facing copy. Internal week anchor only. */
export const WEEK_1_SUNDAY_LABEL = "Sunday, September 13, 2026";
export const TOTAL_FANTASY_WEEKS = 17;
export const REGULAR_SEASON_WEEKS = 14;
export const MAX_SCORING_WEEK = 17;

/** Precomputed Eastern reveal instants (local ISO strings without Z — parsed as local for display only). */
let weekMeta = null;
let calendarPromise = null;
let cachedDefaultWeek = 1;
let cachedSeasonStarted = false;

function parseLocalDate(isoDate) {
  const [y, m, d] = isoDate.split("-").map(Number);
  return new Date(y, m - 1, d);
}

function buildWeekMetaFromApi(payload) {
  if (!payload?.weeks?.length) return buildWeekMetaFallback();
  return payload.weeks.map((w) => ({
    week: w.week,
    sunday: parseLocalDate(w.sunday),
    reveal1: new Date(w.reveal_1pm),
    reveal4: new Date(w.reveal_4pm),
    reveal8: new Date(w.reveal_8pm),
    weekComplete: new Date(w.week_complete),
    phase: w.phase,
  }));
}

function buildWeekMetaFallback() {
  const start = parseLocalDate(WEEK_1_SUNDAY);
  const meta = [];
  for (let week = 1; week <= MAX_SCORING_WEEK; week++) {
    const sunday = new Date(start);
    sunday.setDate(sunday.getDate() + 7 * (week - 1));
    const mk = (hour) => {
      const d = new Date(sunday);
      d.setHours(hour, 0, 0, 0);
      return d;
    };
    meta.push({
      week,
      sunday,
      reveal1: mk(13),
      reveal4: mk(16),
      reveal8: mk(20),
      weekComplete: mk(20),
      phase: week <= REGULAR_SEASON_WEEKS ? "regular" : "playoffs",
    });
  }
  return meta;
}

export function ensureWeekMeta() {
  if (weekMeta) return Promise.resolve(weekMeta);
  if (!calendarPromise) {
    calendarPromise = fetch(`${config.API_URL}/season-calendar`, { credentials: "include" })
      .then((res) => (res.ok ? res.json() : null))
      .then((payload) => {
        weekMeta = buildWeekMetaFromApi(payload);
        if (payload?.current_week != null) {
          cachedDefaultWeek = Number(payload.current_week) || 1;
        }
        cachedSeasonStarted = Boolean(payload?.season_started);
        return weekMeta;
      })
      .catch(() => {
        weekMeta = buildWeekMetaFallback();
        cachedDefaultWeek = getDefaultFantasyWeekSync();
        cachedSeasonStarted = cachedDefaultWeek > 1 || (() => {
          const cutoff = getWeek1ScoringStartsEastern();
          return cutoff ? getNowEastern() >= cutoff : false;
        })();
        return weekMeta;
      });
  }
  return calendarPromise;
}

function getWeekEntry(weekNum) {
  if (!weekMeta) return buildWeekMetaFallback().find((w) => w.week === weekNum);
  return weekMeta.find((w) => w.week === weekNum);
}

/** @deprecated Legacy 1pm/4pm/8pm buckets — kept for roster-lock copy only. */
export function normalizeKickoffSlot(t) {
  const s = String(t || "1pm")
    .toLowerCase()
    .replace(/\s/g, "");
  if (s === "4pm" || s.startsWith("4")) return "4pm";
  if (s === "8pm" || s.startsWith("8")) return "8pm";
  return "1pm";
}

export function parseRevealAtIso(revealAtIso) {
  if (!revealAtIso) return null;
  const d = new Date(revealAtIso);
  return Number.isNaN(d.getTime()) ? null : d;
}

export function isRevealAtPassed(revealAtIso, useTestClock, simulatedTestTime) {
  if (useTestClock) {
    if (
      simulatedTestTime === "after8pm" ||
      simulatedTestTime === "serverReveals" ||
      simulatedTestTime === "allRevealed"
    ) {
      return true;
    }
    return false;
  }
  const reveal = parseRevealAtIso(revealAtIso);
  if (!reveal) return false;
  return Date.now() >= reveal.getTime();
}

export function getNowEastern() {
  return new Date(
    new Date().toLocaleString("en-US", { timeZone: "America/New_York" })
  );
}

export function isSlotRevealed(weekNum, slot, useTestClock, simulatedTestTime) {
  if (useTestClock) {
    const kickoff = normalizeKickoffSlot(slot);
    if (
      simulatedTestTime === "after8pm" ||
      simulatedTestTime === "serverReveals" ||
      simulatedTestTime === "allRevealed"
    ) {
      return true;
    }
    if (simulatedTestTime === "after4pm") return kickoff === "1pm" || kickoff === "4pm";
    if (simulatedTestTime === "after1pm") return kickoff === "1pm";
    return false;
  }
  const entry = getWeekEntry(weekNum);
  if (!entry) return false;
  const now = getNowEastern();
  const kickoff = normalizeKickoffSlot(slot);
  if (kickoff === "1pm") return now >= entry.reveal1;
  if (kickoff === "4pm") return now >= entry.reveal4;
  return now >= entry.reveal8;
}

export function isWeekComplete(weekNum, useTestClock, simulatedTestTime, revealAts = null) {
  if (useTestClock) {
    return (
      simulatedTestTime === "after8pm" ||
      simulatedTestTime === "serverReveals" ||
      simulatedTestTime === "allRevealed"
    );
  }
  if (Array.isArray(revealAts) && revealAts.length > 0) {
    const now = Date.now();
    return revealAts.every((iso) => {
      const d = parseRevealAtIso(iso);
      return d && now >= d.getTime();
    });
  }
  const entry = getWeekEntry(weekNum);
  if (!entry) return false;
  return getNowEastern() >= entry.weekComplete;
}

export function getDefaultFantasyWeekSync() {
  const seasonStart = getWeek1ScoringStartsEastern();
  const now = getNowEastern();
  if (!seasonStart || now < seasonStart) return 1;
  const meta = weekMeta || buildWeekMetaFallback();
  for (let i = meta.length - 1; i >= 0; i -= 1) {
    const weekStart = new Date(meta[i].sunday);
    weekStart.setDate(weekStart.getDate() - 3);
    if (now >= weekStart) return meta[i].week;
  }
  return 1;
}

/** Default week for lineup/scoreboard selectors (current NFL week, or 1 before season). */
export async function resolveDefaultFantasyWeek() {
  await ensureWeekMeta();
  return cachedDefaultWeek || getDefaultFantasyWeekSync();
}

export function isSeasonStartedSync() {
  if (weekMeta) return cachedSeasonStarted;
  const cutoff = getWeek1ScoringStartsEastern();
  return cutoff ? getNowEastern() >= cutoff : false;
}

export function formatWeekSundayLabel(weekNum) {
  const entry = getWeekEntry(weekNum);
  if (!entry) return "";
  return entry.sunday.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

/** Week 1 scoring begins at the first NFL kickoff (Thursday TNF window). */
export function getWeek1ScoringStartsEastern() {
  return parseEasternWallClock(WEEK_1_THURSDAY, "20:15");
}

/** Thursday TNF window when transaction freeze begins for fantasy week N. */
export function fantasyWeekFirstKickoffEastern(weekNum) {
  if (weekNum <= 1) return getWeek1ScoringStartsEastern();
  const entry = getWeekEntry(weekNum);
  if (!entry) return getWeek1ScoringStartsEastern();
  const thursday = new Date(entry.sunday);
  thursday.setDate(thursday.getDate() - 3);
  thursday.setHours(20, 15, 0, 0);
  return thursday;
}

const DAY_NAME_TO_NUM = {
  sunday: 0,
  monday: 1,
  tuesday: 2,
  wednesday: 3,
  thursday: 4,
  friday: 5,
  saturday: 6,
};

export const FCFS_CLOSE_WEEKDAY = 0; // Sunday
export const FCFS_CLOSE_HOUR = 13; // 1:00 PM ET

function normalizeUnlockHour(unlockHour) {
  const h = unlockHour == null ? 5 : Number(unlockHour);
  return Math.max(0, Math.min(23, h));
}

function atWeekdayHour(at, weekday, hour) {
  const candidate = new Date(at);
  const daysAhead = (weekday - candidate.getDay() + 7) % 7;
  candidate.setDate(candidate.getDate() + daysAhead);
  candidate.setHours(hour, 0, 0, 0);
  return candidate;
}

function lastOccurrenceAtOrBefore(at, weekday, hour) {
  let candidate = atWeekdayHour(at, weekday, hour);
  if (candidate > at) {
    candidate = new Date(candidate);
    candidate.setDate(candidate.getDate() - 7);
  }
  return candidate;
}

function nextOccurrenceAfter(at, weekday, hour) {
  let candidate = atWeekdayHour(at, weekday, hour);
  if (candidate <= at) {
    candidate = new Date(candidate);
    candidate.setDate(candidate.getDate() + 7);
  }
  return candidate;
}

/** preseason | fcfs | claim_queue */
export function getTransactionPhase(now, unlockDay = "wednesday", unlockHour = 5) {
  const seasonStart = getWeek1ScoringStartsEastern();
  if (!seasonStart || now < seasonStart) return "preseason";

  const targetDay = DAY_NAME_TO_NUM[(unlockDay || "wednesday").toLowerCase()] ?? 3;
  const hour = normalizeUnlockHour(unlockHour);

  const lastRun = lastOccurrenceAtOrBefore(now, targetDay, hour);
  const lastFcfsClose = lastOccurrenceAtOrBefore(now, FCFS_CLOSE_WEEKDAY, FCFS_CLOSE_HOUR);

  if (lastRun > lastFcfsClose) return "fcfs";
  return "claim_queue";
}

export function isFcfsOpen(unlockDay = "wednesday", unlockHour = 5, now = getNowEastern()) {
  const phase = getTransactionPhase(now, unlockDay, unlockHour);
  return phase === "preseason" || phase === "fcfs";
}

export function isClaimQueuePeriod(unlockDay = "wednesday", unlockHour = 5, now = getNowEastern()) {
  return getTransactionPhase(now, unlockDay, unlockHour) === "claim_queue";
}

export function getNextFcfsClose(now = getNowEastern()) {
  const seasonStart = getWeek1ScoringStartsEastern();
  if (!seasonStart || now < seasonStart) return null;
  return nextOccurrenceAfter(now, FCFS_CLOSE_WEEKDAY, FCFS_CLOSE_HOUR);
}

export function getNextWaiverRun(unlockDay = "wednesday", unlockHour = 5, now = getNowEastern()) {
  const seasonStart = getWeek1ScoringStartsEastern();
  if (!seasonStart) return null;
  const targetDay = DAY_NAME_TO_NUM[(unlockDay || "wednesday").toLowerCase()] ?? 3;
  const hour = normalizeUnlockHour(unlockHour);
  if (now < seasonStart) {
    let first = atWeekdayHour(seasonStart, targetDay, hour);
    if (first < seasonStart) {
      first = new Date(first);
      first.setDate(first.getDate() + 7);
    }
    return first;
  }
  return nextOccurrenceAfter(now, targetDay, hour);
}

/** @deprecated Use getTransactionPhase / isClaimQueuePeriod */
export function getUnlockAfterLockStart(lockStart, unlockDay = "wednesday", unlockHour = 5) {
  if (!lockStart) return null;
  const targetDay = DAY_NAME_TO_NUM[(unlockDay || "wednesday").toLowerCase()] ?? 3;
  const hour = normalizeUnlockHour(unlockHour);
  return nextOccurrenceAfter(lockStart, targetDay, hour);
}

/** Active claim-queue window containing `now`, or null if FCFS/preseason */
export function getActiveTransactionLockWindow(now, unlockDay = "wednesday", unlockHour = 5) {
  if (!isClaimQueuePeriod(unlockDay, unlockHour, now)) return null;
  const targetDay = DAY_NAME_TO_NUM[(unlockDay || "wednesday").toLowerCase()] ?? 3;
  const hour = normalizeUnlockHour(unlockHour);
  const unlockTime = nextOccurrenceAfter(now, targetDay, hour);
  const lockStart = lastOccurrenceAtOrBefore(now, FCFS_CLOSE_WEEKDAY, FCFS_CLOSE_HOUR);
  return { lockStart, unlockTime };
}

export function getNextTransactionLockStart(now) {
  return getNextFcfsClose(now);
}

/** @deprecated Use getWeek1ScoringStartsEastern */
export function getWeek1FirstRevealEastern() {
  return getWeek1ScoringStartsEastern();
}

export function getLastDraftScheduleDateIso() {
  const start = parseLocalDate(WEEK_1_SUNDAY);
  const last = new Date(start);
  last.setDate(last.getDate() + 7 * MAX_SCORING_WEEK - 1);
  return last.toLocaleDateString("en-CA");
}

export function isDraftAfterWeek1Start(draftDate, draftTime) {
  const draftStart = parseEasternWallClock(draftDate, draftTime);
  const week1Start = getWeek1ScoringStartsEastern();
  if (!draftStart || !week1Start) return false;
  return draftStart >= week1Start;
}

export const LATE_DRAFT_WARNING =
  `This draft is after Week 1 scoring begins (${WEEK_1_SCORING_START_DETAIL}). Earlier weeks will not have lineups or scores. Draft before Week 1 for a full regular season.`;

export const PUBLIC_REGISTRATION_CLOSED_MESSAGE =
  `Public league registration is closed for this season. New public leagues are available before Week 1 scoring begins (${WEEK_1_SCORING_START_DETAIL}). You can still create or join a private league.`;

/** True before the first Week 1 NFL kickoff window. */
export function isPublicRegistrationOpen() {
  const cutoff = getWeek1ScoringStartsEastern();
  if (!cutoff) return true;
  return getNowEastern() < cutoff;
}
